import { useEffect, useRef } from 'react';

type Props = {
  /** Lado del footer/header donde vive el canvas: define hacia dónde se funde el patrón */
  side: 'left' | 'right',
  className?: string,
};

// Shader del plexus animado (puntos + líneas que orbitan), portado del demo
const vertexShaderSource = `
  attribute vec2 a_position;
  varying vec2 v_texCoord;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texCoord = a_position * 0.5 + 0.5;
  }
`;

const fragmentShaderFor = (side: 'left' | 'right') => `
  precision highp float;
  varying vec2 v_texCoord;
  uniform float u_time;
  uniform vec2 u_resolution;

  float hash(vec2 p) {
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  vec2 get_point(vec2 id, float offset) {
      float h = hash(id + offset);
      return vec2(cos(u_time * 0.5 + h * 6.28), sin(u_time * 0.5 + h * 6.28)) * 0.4 + 0.5;
  }

  void main() {
      vec2 uv = v_texCoord;
      uv.x *= u_resolution.x / u_resolution.y;

      vec2 g_uv = uv * 3.0;
      vec2 id = floor(g_uv);
      vec2 f_uv = fract(g_uv);

      float minDist = 1.0;
      float line = 0.0;

      for(float y = -1.0; y <= 1.0; y++) {
          for(float x = -1.0; x <= 1.0; x++) {
              vec2 neighbor = vec2(x, y);
              vec2 point = get_point(id + neighbor, 0.0);
              vec2 diff = neighbor + point - f_uv;
              float dist = length(diff);
              minDist = min(minDist, dist);

              if(x <= 0.0 && y <= 0.0) {
                 for(float ny = -1.0; ny <= 1.0; ny++) {
                     for(float nx = -1.0; nx <= 1.0; nx++) {
                         if(nx == 0.0 && ny == 0.0) continue;
                         vec2 n_point = get_point(id + neighbor + vec2(nx, ny), 0.0);

                         vec2 p1 = neighbor + point;
                         vec2 p2 = neighbor + vec2(nx, ny) + n_point;
                         vec2 pa = f_uv - p1;
                         vec2 ba = p2 - p1;
                         float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
                         float d = length(pa - ba * h);

                         line = max(line, (1.0 - smoothstep(0.0, 0.015, d)) * 0.4);
                     }
                 }
              }
          }
      }

      float dots = 1.0 - smoothstep(0.04, 0.06, minDist);

      // Colores del diseño: navy hacia celeste
      vec3 color = mix(vec3(0.176, 0.192, 0.443), vec3(0.4, 0.7, 1.0), dots + line);

      // Fundido hacia el lado opuesto para empalmar con el fondo sólido
      float fadeX = ${side === 'left' ? '1.0 - v_texCoord.x' : 'v_texCoord.x'};
      float alpha = smoothstep(0.0, 0.8, fadeX);

      gl_FragColor = vec4(color, alpha * 0.8);
  }
`;

function createShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

const PlexusCanvas = ({ side, className }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl');
    if (!gl) return;

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderFor(side));
    if (!vertexShader || !fragmentShader) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    const positions = [
      -1.0, -1.0,
       1.0, -1.0,
      -1.0,  1.0,
      -1.0,  1.0,
       1.0, -1.0,
       1.0,  1.0,
    ];
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

    const positionAttributeLocation = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(positionAttributeLocation);
    gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

    const timeLocation = gl.getUniformLocation(program, 'u_time');
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');

    const resizeCanvasToDisplaySize = () => {
      const displayWidth = canvas.clientWidth;
      const displayHeight = canvas.clientHeight;
      if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
        canvas.width = displayWidth;
        canvas.height = displayHeight;
      }
    };

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    let rafId = 0;
    const render = (time: number) => {
      time *= 0.001;

      resizeCanvasToDisplaySize();
      gl.viewport(0, 0, canvas.width, canvas.height);

      gl.clearColor(0.0, 0.0, 0.0, 0.0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      gl.uniform1f(timeLocation, time);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);

      gl.drawArrays(gl.TRIANGLES, 0, 6);

      rafId = requestAnimationFrame(render);
    };
    rafId = requestAnimationFrame(render);

    return () => cancelAnimationFrame(rafId);
  }, [side]);

  return <canvas className={className} ref={canvasRef} aria-hidden="true" />;
};

export default PlexusCanvas;
